import { useState, useMemo } from 'react';
import { JSONPath } from 'jsonpath-plus';
import { Copy, AlertCircle } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, Field } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { copyToClipboard } from '@/lib/utils';
import { toast } from 'sonner';

const SAMPLE_DATA = JSON.stringify(
  {
    org: 'Utility OS',
    location: { city: 'Bengaluru', state: 'KA' },
    employees: [
      { name: 'Aarav Sharma', role: 'Engineer', salary: 1800000, active: true },
      { name: 'Priya Mehta', role: 'Designer', salary: 1500000, active: true },
      { name: 'Rohan Iyer', role: 'PM', salary: 2200000, active: false },
      { name: 'Diya Nair', role: 'Engineer', salary: 1700000, active: true },
    ],
    products: ['Workspace', 'Agent', 'Workflows'],
  },
  null,
  2,
);

const EXAMPLES = [
  { path: '$.org', desc: 'Top-level field' },
  { path: '$..name', desc: 'All names recursively' },
  { path: '$.employees[*].role', desc: 'All roles' },
  { path: '$.employees[?(@.salary > 1700000)]', desc: 'Filter high earners' },
  { path: '$.employees[?(@.active == true)].name', desc: 'Active employees' },
  { path: '$.products[0:2]', desc: 'First two products' },
  { path: '$..*', desc: 'Every leaf' },
];

export function JsonPath() {
  const [data, setData] = useState(SAMPLE_DATA);
  const [path, setPath] = useState('$.employees[?(@.active == true)].name');

  const result = useMemo(() => {
    try {
      const obj = JSON.parse(data);
      const out = JSONPath({ path, json: obj });
      return { ok: true as const, output: out, count: Array.isArray(out) ? out.length : 1 };
    } catch (e) {
      return { ok: false as const, error: e instanceof Error ? e.message : 'Error' };
    }
  }, [data, path]);

  return (
    <div>
      <ToolHeader
        toolId="jsonpath"
        title="JSONPath query"
        description="Query JSON with JSONPath. Filters, recursion, slicing — like jq but simpler."
        badges={
          result.ok ? (
            <Badge variant="success">{result.count} match{result.count === 1 ? '' : 'es'}</Badge>
          ) : (
            <Badge variant="danger">error</Badge>
          )
        }
      />

      <Card className="mb-4">
        <Field label="JSONPath expression" hint="Use $.path syntax">
          <Input
            value={path}
            onChange={(e) => setPath(e.target.value)}
            className="font-mono"
          />
        </Field>
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {EXAMPLES.map((ex) => (
            <button
              key={ex.path}
              onClick={() => setPath(ex.path)}
              className="px-2 py-0.5 text-2xs font-mono rounded-md border border-border hover:border-accent hover:bg-accent/5 transition"
              title={ex.desc}
            >
              {ex.path}
            </button>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="!p-0">
          <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
            JSON
          </div>
          <Textarea
            mono
            value={data}
            onChange={(e) => setData(e.target.value)}
            className="!border-0 !rounded-t-none min-h-[400px] !bg-transparent focus-visible:!ring-0"
          />
        </Card>
        <Card className="!p-0">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
            <span className="text-xs font-medium text-fg-muted">Result</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={async () => {
                if (!result.ok) return;
                const ok = await copyToClipboard(JSON.stringify(result.output, null, 2));
                if (ok) toast.success('Copied');
              }}
              disabled={!result.ok}
            >
              <Copy size={11} />
            </Button>
          </div>
          {result.ok ? (
            <pre className="font-mono text-[13px] leading-relaxed p-4 overflow-auto min-h-[400px] max-h-[480px]">
              <code>{JSON.stringify(result.output, null, 2)}</code>
            </pre>
          ) : (
            <div className="p-4 min-h-[400px]">
              <div className="flex items-start gap-2 text-danger text-sm">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <code className="font-mono break-all">{result.error}</code>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

export default JsonPath;
